/**
 * Pins the document's flow shell to the browser `visualViewport` by writing two CSS
 * custom properties on `<html>`:
 *
 *   --vvh           — visualViewport.height in px (visible area above the on-screen keyboard)
 *   --vv-offset-top — visualViewport.offsetTop in px (how far the visual viewport has been
 *                     slid down inside the layout viewport, e.g. when iOS Safari focus-scrolls
 *                     a tapped input above the keyboard)
 *
 * `apps/web/src/styles/index.module.css :appFlowWrapper` consumes both via
 * `position: fixed; top: var(--vv-offset-top); height: var(--vvh)` so the entire flow shell
 * tracks the visible area exactly. This is the only reliable way to neutralise the iOS
 * Safari "white space below content / broken scroll" bug on multi-field keyboard steps:
 *
 *   - iOS does NOT (yet) honour `interactive-widget=resizes-content` in the viewport meta.
 *   - With the default behaviour the layout viewport stays at full window height while the
 *     visual viewport shrinks above the keyboard. When an input is focused, iOS slides the
 *     visual viewport down inside the layout viewport (offsetTop > 0) to push the input
 *     above the keyboard. A regular `position: fixed; top: 0` element stays anchored to
 *     the LAYOUT viewport, so iOS slides it up out of the visible area — exposing empty
 *     layout-viewport space below it (the "white space"). Tracking offsetTop and applying
 *     it as `top` keeps the shell pinned to the VISUAL viewport instead.
 *
 * Web-only; safe to call multiple times (idempotent listener registration).
 */

let installed = false;

export function installVisualViewportPin(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;

  const root = document.documentElement;
  const vv = window.visualViewport;

  const update = (): void => {
    const height = vv && vv.height > 0 ? vv.height : window.innerHeight;
    const offsetTop = vv && Number.isFinite(vv.offsetTop) ? vv.offsetTop : 0;
    root.style.setProperty("--vvh", `${height}px`);
    root.style.setProperty("--vv-offset-top", `${offsetTop}px`);
  };

  update();

  vv?.addEventListener("resize", update);
  vv?.addEventListener("scroll", update);
  window.addEventListener("resize", update);
  window.addEventListener("orientationchange", update);

  // iOS Safari sometimes fires focus before visualViewport reports the keyboard;
  // re-read on the next frame so var values aren't stale during the keyboard animation.
  const onFocusChange = (): void => {
    requestAnimationFrame(update);
  };
  document.addEventListener("focusin", onFocusChange, true);
  document.addEventListener("focusout", onFocusChange, true);
}
